// Optional trip recording — port of TrailBivvy/App/Services/TripRecorder.swift. Stored in IndexedDB; nothing leaves
// the device. A recording survives a reload or a closed tab: it is restored at launch (paused if it was paused), and
// the gap while the app was closed starts a new segment so it never counts as walking.

import { signal } from '@preact/signals';
import type { EpochMs, OvernightCategory, TrackPoint, TrackStatistics, UserTrip } from '../core/models';
import { TrackStatisticsAccumulator, zeroTrackStatistics } from '../nav';
import { newId, type TripRepository } from './repositories';

const pausedTripKey = 'tb.recording.pausedTripID';

export class TripRecorder {
  readonly isRecording = signal(false);
  readonly isPaused = signal(false);
  readonly currentTrip = signal<UserTrip | undefined>(undefined);
  readonly statistics = signal<TrackStatistics>(zeroTrackStatistics());
  readonly breadcrumb = signal<TrackPoint[]>([]);
  readonly lastError = signal<string | undefined>(undefined);

  /** Called when a recording finishes, so the walking-performance model can learn. */
  onFinished: ((trip: UserTrip) => void) | undefined;

  private accumulator = new TrackStatisticsAccumulator();
  private lastSave = 0;
  /** Full-track saves are O(n): every 2 minutes, plus on pause/stop/hide. */
  private readonly saveInterval = 120_000;

  constructor(
    private readonly repository: TripRepository,
    private readonly now: () => EpochMs,
  ) {}

  start(name: string, trailID: string | undefined): void {
    if (this.isRecording.value) return;
    this.accumulator = new TrackStatisticsAccumulator();
    this.currentTrip.value = {
      id: newId(),
      name,
      startedAt: this.now(),
      trailID,
      trackPoints: [],
      overnightStops: [],
      completedStageIDs: [],
      statistics: zeroTrackStatistics(),
    };
    this.statistics.value = zeroTrackStatistics();
    this.breadcrumb.value = [];
    this.isRecording.value = true;
    this.isPaused.value = false;
    void this.persist(true);
  }

  pause(): void {
    this.isPaused.value = true;
    this.storePausedState();
    void this.persist(true);
  }

  resume(): void {
    this.isPaused.value = false;
    // The pause gap (bus, cable car, a night at a hut) must not count as walking.
    this.accumulator.startNewSegment();
    this.storePausedState();
  }

  /** Saves the track now (the page is being hidden). */
  flush(): Promise<void> {
    return this.persist(true);
  }

  async stop(): Promise<UserTrip | undefined> {
    const trip = this.currentTrip.value;
    if (!this.isRecording.value || !trip) return undefined;
    const finished: UserTrip = {
      ...trip,
      endedAt: this.now(),
      trackPoints: [...this.accumulator.acceptedPoints],
      statistics: this.accumulator.statistics,
    };
    this.currentTrip.value = finished;
    this.isRecording.value = false;
    this.isPaused.value = false;
    this.storePausedState();
    await this.persist(true);
    this.onFinished?.(finished);
    this.currentTrip.value = undefined;
    return finished;
  }

  markOvernightStop(category: OvernightCategory, name?: string): void {
    const trip = this.currentTrip.value;
    const last = this.accumulator.acceptedPoints[this.accumulator.acceptedPoints.length - 1];
    if (!trip || !last) return;
    this.currentTrip.value = {
      ...trip,
      overnightStops: [...trip.overnightStops, { id: newId(), coordinate: last.coordinate, date: this.now(), category, name }],
    };
    void this.persist(true);
  }

  markStageCompleted(stageID: string): void {
    const trip = this.currentTrip.value;
    if (!trip || trip.completedStageIDs.includes(stageID)) return;
    this.currentTrip.value = { ...trip, completedStageIDs: [...trip.completedStageIDs, stageID] };
    void this.persist(true);
  }

  handle(fix: TrackPoint): void {
    if (!this.isRecording.value || this.isPaused.value) return;
    if (this.accumulator.append(fix)) {
      this.statistics.value = this.accumulator.statistics;
      this.breadcrumb.value = [...this.accumulator.acceptedPoints];
      void this.persist(false);
    }
  }

  /** Continues an interrupted recording after a reload. */
  async restoreActiveRecording(): Promise<void> {
    const trip = await this.repository.activeRecording().catch(() => undefined);
    if (!trip) return;
    this.currentTrip.value = trip;
    this.accumulator = new TrackStatisticsAccumulator();
    for (const point of trip.trackPoints) this.accumulator.append(point);
    this.statistics.value = this.accumulator.statistics;
    this.breadcrumb.value = [...this.accumulator.acceptedPoints];
    this.isRecording.value = true;
    let pausedID: string | null = null;
    try {
      pausedID = localStorage.getItem(pausedTripKey);
    } catch {
      pausedID = null;
    }
    this.isPaused.value = pausedID === trip.id;
    this.accumulator.startNewSegment();
  }

  private storePausedState(): void {
    try {
      const id = this.currentTrip.value?.id;
      if (this.isPaused.value && id) localStorage.setItem(pausedTripKey, id);
      else localStorage.removeItem(pausedTripKey);
    } catch {
      // storage blocked: a reload resumes recording instead of staying paused
    }
  }

  private async persist(force: boolean): Promise<void> {
    const trip = this.currentTrip.value;
    if (!trip) return;
    const now = this.now();
    if (!force && now - this.lastSave < this.saveInterval) return;
    this.lastSave = now;
    try {
      await this.repository.save({ ...trip, trackPoints: [...this.accumulator.acceptedPoints], statistics: this.accumulator.statistics });
      this.lastError.value = undefined;
    } catch (error) {
      this.lastError.value = error instanceof Error ? error.message : String(error);
    }
  }
}
